import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Nav } from "../components/Nav";
import { api, API_URL } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { RefreshCw, Trash2, ExternalLink, ArrowLeft, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminDeadLinks() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [dead, setDead] = useState({ items: [], total_dead: 0 });
    const [scanning, setScanning] = useState(false);
    const [purging, setPurging] = useState(false);
    const [lastScan, setLastScan] = useState(null);

    useEffect(() => {
        if (!loading && (!user || user.role !== "admin")) navigate("/dashboard");
    }, [user, loading, navigate]);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/dead-links?limit=200");
            setDead(data);
        } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    };

    useEffect(() => { if (user?.role === "admin") load(); }, [user]);

    const scan = async () => {
        setScanning(true);
        try {
            const { data } = await api.post("/admin/links/scan-dead?limit=200&concurrency=10");
            setLastScan(data);
            if (data.checked === 0) {
                toast.success("Everything was scanned within the last 24h — nothing new to check right now.");
            } else {
                toast.success(`Scanned ${data.checked} · +${data.newly_dead} newly dead · ${data.revived} revived`);
            }
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Scan failed");
        } finally { setScanning(false); }
    };

    const deleteOne = async (linkId, slug) => {
        if (!window.confirm(`Delete /${slug} permanently and free the slug for reuse?`)) return;
        try {
            await api.delete(`/admin/links/${linkId}`);
            toast.success(`Deleted /${slug} — slug is now available`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    };

    const purgeAll = async () => {
        if (!window.confirm(`Delete all ${dead.total_dead} dead links and free their slugs? This cannot be undone.`)) return;
        setPurging(true);
        try {
            const { data } = await api.post("/admin/dead-links/purge");
            toast.success(`Purged ${data.deleted} dead links · freed ${data.slugs_freed.length} slugs for reuse`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Purge failed");
        } finally { setPurging(false); }
    };

    if (loading || !user) return null;

    return (
        <div className="App min-h-screen">
            <Nav />
            <div className="mx-auto max-w-6xl px-6 py-10">
                <Link to="/admin" className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary" data-testid="back-to-admin-link">
                    <ArrowLeft className="h-4 w-4" /> Back to Admin
                </Link>

                <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <h1 className="font-display text-4xl font-bold tracking-tighter">Dead links</h1>
                        <p className="mt-2 text-sm text-muted-foreground">
                            Links whose destination returned 4xx/5xx or timed out on the last scan. Deleting one frees its slug for reuse.
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button onClick={scan} disabled={scanning} data-testid="scan-dead-btn">
                            {scanning
                                ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Scanning…</>)
                                : (<><RefreshCw className="mr-2 h-4 w-4" /> Scan now</>)}
                        </Button>
                        {dead.total_dead > 0 && (
                            <Button variant="destructive" onClick={purgeAll} disabled={purging} data-testid="purge-all-btn">
                                {purging
                                    ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Purging…</>)
                                    : (<><Trash2 className="mr-2 h-4 w-4" /> Purge all ({dead.total_dead})</>)}
                            </Button>
                        )}
                    </div>
                </div>

                {lastScan && (
                    <div className="mb-6 rounded-lg border border-border bg-card p-4 text-sm">
                        <div className="flex flex-wrap gap-6">
                            <div><span className="text-muted-foreground">Checked</span> <strong className="ml-2 font-display text-2xl">{lastScan.checked}</strong></div>
                            <div><span className="text-muted-foreground">Newly dead</span> <strong className="ml-2 font-display text-2xl text-destructive">+{lastScan.newly_dead}</strong></div>
                            <div><span className="text-muted-foreground">Revived</span> <strong className="ml-2 font-display text-2xl text-green-600">{lastScan.revived}</strong></div>
                        </div>
                        {lastScan.checked === 0 && (
                            <p className="mt-3 text-xs text-muted-foreground">
                                All links were checked within the last 24 hours — nothing new to sweep. Come back later or create new links to test.
                            </p>
                        )}
                    </div>
                )}

                {dead.total_dead === 0 ? (
                    <div className="rounded-lg border-2 border-dashed border-border bg-card p-16 text-center text-muted-foreground" data-testid="dead-links-empty">
                        <AlertTriangle className="mx-auto mb-4 h-8 w-8 opacity-30" />
                        <p>No dead links right now.</p>
                        <p className="mt-1 text-xs">Click <strong>Scan now</strong> to sweep the next 200 links.</p>
                    </div>
                ) : (
                    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid="dead-links-table">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Slug</TableHead>
                                    <TableHead>Destination</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Dead since</TableHead>
                                    <TableHead className="w-32"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {dead.items.map((l) => (
                                    <TableRow key={l.link_id} data-testid={`dead-row-${l.slug}`}>
                                        <TableCell className="font-mono text-xs">{l.slug}</TableCell>
                                        <TableCell className="max-w-md truncate">
                                            <a href={l.long_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-primary">
                                                {l.long_url}
                                                <ExternalLink className="h-3 w-3" />
                                            </a>
                                        </TableCell>
                                        <TableCell>
                                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-mono ${
                                                l.last_status === 0
                                                    ? "bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300"
                                                    : "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300"
                                            }`}>
                                                {l.last_status === 0 ? "TIMEOUT" : l.last_status}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-xs text-muted-foreground">
                                            {l.dead_since ? new Date(l.dead_since).toLocaleString() : "—"}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="ghost" size="sm"
                                                onClick={() => deleteOne(l.link_id, l.slug)}
                                                aria-label={`Delete ${l.slug} and free the slug`}
                                                title="Delete and free slug"
                                                data-testid={`delete-dead-${l.slug}`}
                                            >
                                                <Trash2 className="h-4 w-4 text-destructive" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>
        </div>
    );
}
